import {
  CustomFilterUnion,
  ConfigEntity,
  TCustomFilter,
  CFChangeEvent,
} from "./types";
import { f } from "../../../util/f";
import CustomFilterItem from "./CustomFilterItem";
import { isEmpty, random, sortBy } from "lodash";
import SuperDropdown from "../../dropdown/SuperDropdown";
import CustomFilterDropdownListItem from "../../dropdown/CustomFilterDropdownListItem";

export default function CustomFilterSubItem<
  C extends CustomFilterUnion,
>(props: {
  filterConfig: ConfigEntity<C>;
  onChangeFilter: (event: CFChangeEvent) => void;
  settings: TCustomFilter[] | undefined;
}) {
  // destruction!!
  const { filterConfig, onChangeFilter, settings } = props;

  // derived stateful things
  const subFilters: TCustomFilter[] = settings || [];

  // assert and sort config values
  const configValues = filterConfig.component.values as ConfigEntity<C>[];
  const sortedConfigValues = sortBy(configValues, ["displayName"]);

  // handling of handlers
  function addSubFilterHandler(id: string, configKey: string) {
    const updatedSubFilters: TCustomFilter[] = [
      ...subFilters,
      {
        id: id,
        configKey: configKey,
      },
    ];

    const canBuildFilter = Boolean(
      updatedSubFilters.filter((filter) => !isEmpty(filter.filterObj)).length,
    );

    const filterString = canBuildFilter
      ? f<any>()
          .addFilter(filterConfig.queryKey, {
            some: {
              and: updatedSubFilters.flatMap((sf) =>
                sf.filterObj ? [sf.filterObj] : [],
              ),
            },
          })
          .build()
      : "";

    const filterObj = canBuildFilter
      ? {
          [filterConfig.queryKey]: {
            some: {
              and: updatedSubFilters.flatMap((sf) =>
                sf.filterObj ? [sf.filterObj] : [],
              ),
            },
          },
        }
      : undefined;

    const event: CFChangeEvent = {
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: updatedSubFilters,
    };

    onChangeFilter(event);
  }

  function onSubChangeHandler(id: string, event: CFChangeEvent) {
    // update filter if id exists in custom filters
    for (const sf of subFilters) {
      if (sf.id === id) {
        sf.filterString = event.filterString;
        sf.filterObj = event.filterObj;
        sf.settings = event.filterSettings;
        break;
      }
    }

    // filter out any filters without a valid filter object
    const canBuildFilter = Boolean(
      subFilters.filter((filter) => !isEmpty(filter.filterObj)).length,
    );

    event.filterString = canBuildFilter
      ? f<any>()
          .addFilter(filterConfig.queryKey, {
            some: {
              and: subFilters.flatMap((sf) =>
                sf.filterObj ? [sf.filterObj] : [],
              ),
            },
          })
          .build()
      : "";

    event.filterSettings = subFilters;

    onChangeFilter(event);
  }

  function onSubRemoveHandler(id: string) {
    // filter out the removed id and any filters without a valid filter object
    const updatedFilters = subFilters.filter((filter) => filter.id !== id);

    const canBuildFilter = Boolean(
      updatedFilters.filter((filter) => !isEmpty(filter.filterObj)).length,
    );

    const filterString = canBuildFilter
      ? f<any>()
          .addFilter(filterConfig.queryKey, {
            some: {
              and: updatedFilters.flatMap((sf) =>
                sf.filterObj ? [sf.filterObj] : [],
              ),
            },
          })
          .build()
      : "";

    const filterObj = canBuildFilter
      ? {
          [filterConfig.queryKey]: {
            some: {
              and: updatedFilters.flatMap((sf) =>
                sf.filterObj ? [sf.filterObj] : [],
              ),
            },
          },
        }
      : undefined;

    const event: CFChangeEvent = {
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: updatedFilters,
    };

    onChangeFilter(event);
  }

  return (
    <div className="c-custom-filter__content">
      {subFilters.map((sf) => {
        return (
          <CustomFilterItem
            filterConfig={configValues.find((conf) => conf.id === sf.configKey)}
            onChangeFilter={(event) => {
              if (event.filterObj) {
                onSubChangeHandler(sf.id, event);
              }
            }}
            onRemoveFilter={() => {
              onSubRemoveHandler(sf.id);
            }}
            settings={sf.settings}
            key={sf.id}
          />
        );
      })}

      <SuperDropdown
        itemsIdKey="id"
        items={sortedConfigValues}
        filterKeys={["displayName"]}
        itemToString={(item) => item?.displayName ?? ""}
        placeholder="Tilføj felt ..."
        classNameInput={`dashed`}
        clearInputAfterSelection={true}
        onSelectedItemEvent={(event) => {
          if (event.selectedItem) {
            const id = random(0, 100000);
            addSubFilterHandler(String(id), event.selectedItem?.id);
          }
        }}
        itemToElement={(item) => (
          <CustomFilterDropdownListItem
            label={item.displayName}
            chipLabel={item.category}
          />
        )}
      />
    </div>
  );
}
